import { theme } from 'antd'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import { statusDictionary } from './StatusDictionaryStore'
import { STATUS_FIELD, StatusFilterBarStore } from './StatusFilterBarStore'

import type { StatusListContextLike } from './StatusFilterBarStore'
import type { CSSProperties } from 'react'

import './StatusFilterBar.css'

export interface StatusOption {
    value: string
    label: string
    color?: string
}

interface ListContextLike extends StatusListContextLike {
    variant?: string | null
    setFilterOne?: (key: string, value: unknown) => void
    setCurrentPage?: (page: number) => void
    actions?: { load?: (forceRefresh?: boolean) => void }
    eventManager?: {
        on?: (event: string, key: string, handler: () => void) => void
        off?: (event: string, key: string) => void
    }
}

interface FilterFormLike {
    values?: Record<string, unknown>
    getField?: (path: string) => unknown
}

/**
 * Поле статусу ресурсу — те, з чого рядок бере варіанти.
 *
 * `statusResource` — ключ ресурсу довідника статусів (`Setting.ListClientStatus`):
 * варіанти тоді приходять із довідника (код, назва, колір, `sorting`), а
 * список фільтрується за `status.code` (`ListRequestTransformerTask`). Без
 * нього — enum-статус із переліком `options` у самому ресурсі (співробітники).
 */
export interface StatusFieldLike {
    type?: string
    options?: StatusOption[]
    statusResource?: string
    /**
     * Лише ці коди довідника — плашками: сторінка, чий префільтр уже звужує
     * статуси («Рейси» заявок, `SPEC-021` §8.2), не показує порожніх плашок
     */
    statusCodes?: readonly string[]
}

/** Звідки рядок бере варіанти: перелік у ресурсі або довідник статусів. */
export type StatusSource =
    | { kind: 'options'; options: StatusOption[] }
    | { kind: 'dictionary'; resource: string; codes?: readonly string[] }

export interface StatusFilterCrudContextLike {
    isFilter?: boolean
    resource?: { fields?: Record<string, StatusFieldLike | undefined> }
    listContext?: ListContextLike
    filterFormContext?: FilterFormLike
}

/** Ключ підписки на завантаження списку — один на рядок. */
const RELOAD_HANDLER = 'StatusFilterBarCounts'

/**
 * Звідки брати статуси, якщо рядок на цьому списку доречний; інакше `null`.
 *
 * Рядок з'являється сам на кожному реєстрі, у ресурсу якого є поле `status`
 * типу `status` — з переліком варіантів (enum) або з довідником
 * (`statusResource`). Не з'являється там, де він був би зайвим:
 *
 *  - **у вкладках карток** (`isFilter: false`) — там вибірка вже звужена
 *    батьком, і рядок із шести плашок над трьома рядками лише займає місце;
 *  - **коли статус зафіксовано префільтром сторінки** — перемикати нема що;
 *  - **не в табличному режимі** — канбан будує колонки з власних запитів і
 *    фільтр списку не читає, тож плашка над ним показувала б одне, а дошка —
 *    інше.
 */
export const statusSourceFor = (crudContext: StatusFilterCrudContextLike | undefined): StatusSource | null => {
    const field = crudContext?.resource?.fields?.[STATUS_FIELD]

    if (field?.type !== 'status') return null
    if (crudContext?.isFilter === false) return null
    if (crudContext?.listContext?.prefilter?.[STATUS_FIELD] !== undefined) return null

    if (typeof field.statusResource === 'string' && field.statusResource !== '') {
        return {
            kind: 'dictionary',
            resource: field.statusResource,
            ...(Array.isArray(field.statusCodes) ? { codes: field.statusCodes } : {}),
        }
    }

    const options = field.options

    return Array.isArray(options) && options.length > 0 ? { kind: 'options', options } : null
}

interface Props {
    crudContext: StatusFilterCrudContextLike
    source: StatusSource
}

/**
 * Рядок статусів над таблицею: «Всі» і по плашці на кожен статус, з числом
 * записів. Клік фільтрує таблицю, «Всі» знімає фільтр. За замовчуванням — «Всі».
 *
 * Значення плашки — код статусу: за кодом бек і фільтрує, і рахує
 * лічильники (`byStatus`), тож у довідникових статусах плашка, фільтр і цифра
 * говорять про одне й те саме, навіть коли назву статусу змінили.
 *
 * ⚠️ Вибраний статус живе **в тому самому `filter.status` списку**, що й поле
 * «Статус» бічної панелі фільтрів, — другого стану тут немає. Тому рядок і
 * панель не можуть розійтися: вибір статусу в панелі підсвічує плашку, а
 * «Очистити» в панелі повертає рядок на «Всі».
 *
 * ⚠️ Вибір дзеркалиться й у саму форму панелі. Без цього її «Фільтрувати»
 * накладає власні значення поверх фільтра списку — і статус, колись обраний у
 * панелі, тихо повертався б поверх плашки.
 *
 * ⚠️ Дзеркало пишеться **тихо**, прямо в `values` форми, а не через
 * `setValue`. `setValue` запускає `onChange`, на який ядрова панель повісила
 * власне «застосувати й завантажити», — і кожен клік по плашці давав би два
 * запити списку, причому перший ми тут-таки скасовували б.
 *
 * ⚠️ Список вантажиться з `forceRefresh`. Ядровий `restClient` кешує
 * завершені GET-відповіді за ключем запиту, і повернення на «Всі» показувало б
 * сторінку, завантажену ще до кліку, — без змін, що сталися відтоді. Ядрова
 * «Очистити» в панелі робить так само.
 *
 * ⚠️ Перемикання статусу повертає список **на першу сторінку**. Ядрова панель
 * фільтрів цього не робить, і фільтр, застосований на третій сторінці «Всіх»,
 * показує порожню таблицю там, де записів на одну сторінку.
 */
export const StatusFilterBar = observer(({ crudContext, source }: Props) => {
    const { token } = theme.useToken()
    const listContext = crudContext.listContext

    const [store] = useState(() => new StatusFilterBarStore(listContext ?? {}))
    const dictionary = source.kind === 'dictionary' ? statusDictionary(source.resource) : null

    // Довідник перечитуємо на кожне відкриття реєстру: адміністратор міг
    // перефарбувати чи перейменувати статус
    useEffect(() => {
        void dictionary?.refresh()
    }, [dictionary])

    useEffect(() => {
        const em = listContext?.eventManager

        // Підписка на кожне завантаження списку: так лічильники оновлюються
        // і після фільтра, і після переходу в картці, який змінив статус
        em?.on?.('onAfterRequest', RELOAD_HANDLER, () => store.scheduleRefresh())
        store.scheduleRefresh()

        return () => {
            em?.off?.('onAfterRequest', RELOAD_HANDLER)
            store.dispose()
        }
    }, [listContext, store])

    const current = listContext?.filter?.[STATUS_FIELD] as string | undefined
    const variant = listContext?.variant

    if (!listContext || (variant && variant !== 'table')) return null

    // Доки довідник не прийшов, рядка немає зовсім: самотня «Всі» на мить, а
    // потім стрибок таблиці вниз читалися б як збій
    if (dictionary && !dictionary.isLoaded) return null

    const codes = source.kind === 'dictionary' ? source.codes : undefined
    const options = dictionary
        ? dictionary.options.filter((option) => !codes || codes.includes(option.value))
        : source.kind === 'options'
          ? source.options
          : []

    // Бек рахує плашки без умови на статус — і «Всі» теж. Коли сторінка
    // показує лише частину статусів (префільтр «Рейсів»), «Всі» — сума її плашок,
    // інакше цифра обіцяла б рядки, яких таблиця не покаже
    const allCount = codes ? codes.reduce((sum, code) => sum + (store.counts[code] ?? 0), 0) : store.total

    const select = (value: string | undefined): void => {
        if (value === current) return

        listContext.setCurrentPage?.(1)
        listContext.setFilterOne?.(STATUS_FIELD, value)

        // Дзеркало в панель — лише якщо поле «Статус» у ній є: інакше її
        // «Фільтрувати» статусу не чіпає й повернути старий не може
        const form = crudContext.filterFormContext
        const values = form?.values

        if (values && form?.getField?.(STATUS_FIELD)) {
            runInAction(() => {
                if (value === undefined) delete values[STATUS_FIELD]
                else values[STATUS_FIELD] = value
            })
        }

        listContext.actions?.load?.(true)
    }

    const palette = {
        '--wl-status-bar-border': token.colorBorderSecondary,
        '--wl-status-bar-border-hover': token.colorBorder,
        '--wl-status-bar-border-active': token.colorText,
        '--wl-status-bar-bg': token.colorBgContainer,
        '--wl-status-bar-text': token.colorText,
        '--wl-status-bar-count-bg': token.colorFillSecondary,
        '--wl-status-bar-count-text': token.colorTextSecondary,
    } as CSSProperties

    const pill = (key: string, value: string | undefined, label: string, color: string | undefined) => {
        const isActive = value === current

        // Доки лічильники не прийшли — без числа, а не нуль: нуль читався б як
        // «записів немає». Після приходу статус без записів — чесний нуль
        const count =
            store.total === null ? null : value === undefined ? allCount : (store.counts[value] ?? 0)

        return (
            <button
                key={key}
                type="button"
                className={`wl-status-bar-pill${isActive ? ' is-active' : ''}`}
                aria-pressed={isActive}
                onClick={() => select(value)}
            >
                {color ? (
                    <span className="wl-status-bar-dot" style={{ backgroundColor: color.trim() }} />
                ) : null}
                <span className="wl-status-bar-label">{label}</span>
                {count !== null ? <span className="wl-status-bar-count">{count}</span> : null}
            </button>
        )
    }

    return (
        <div className="wl-status-bar" style={palette} role="group" aria-label="Фільтр за статусом">
            {pill('all', undefined, 'Всі', undefined)}
            {options.map((option) => pill(option.value, option.value, option.label, option.color))}
        </div>
    )
})
